import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import React from 'react';

import Panel from 'react-bootstrap/lib/Panel';

import {
  faRocket,
  faRuler,
  faList,
  faChartLine,
  faDollarSign,
} from '@fortawesome/free-solid-svg-icons';

import Link from '../../../../../components/Link';
import PageLayout from '../../../../../components/PageLayout';

import ShadowerLink from '../../../../../components/ShadowerLink';

import Navbar from '../../../../../components/Handbook/Navbar';

import s from '../../HandbookPage.scss';

class InteractivityLevelPage extends React.Component {
  render() {
    return (
      <PageLayout className={s.page}>
        <div className={s.container}>
          <div className={s.navbar}>
            <div className={s.title}>Shadowing Handbook</div>
            <Navbar
              navbarIcon={faRocket}
              navbarSection="Getting Started"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/welcome-to-shadower"
                  >
                    Welcome to Shadower
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/how-shadower-works"
                  >
                    How Shadower Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/working-with-businesses"
                  >
                    Working with Businesses
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/getting-started/shadowing-guidelines"
                  >
                    Shadowing Guidelines
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faRuler}
              navbarSection="Planning a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/choosing-a-topic"
                  >
                    Choosing a Topic
                  </Link>
                  <Link
                    className={cx(s.link, s.active)}
                    to="/handbook/planning-a-session/interactivity-level"
                  >
                    Interactivity Level
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/session-type"
                  >
                    Session Type
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/planning-a-session/providing-resources"
                  >
                    Providing Resources
                  </Link>
                </div>
              }
              overrideDefault
            />

            <Navbar
              navbarIcon={faList}
              navbarSection="Managing a Session"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/expectations"
                  >
                    Expectations
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/session-flow"
                  >
                    Session Flow
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/managing-a-session/maximizing-your-time"
                  >
                    Maximizing Your Time
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faChartLine}
              navbarSection="Promoting Your Profile"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-the-platform-works"
                  >
                    How the Platform Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/optimizing-your-profile"
                  >
                    Optimizing Your Profile
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/marketing-yourself"
                  >
                    Marketing Yourself
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/promoting-your-profile/how-to-build-a-following"
                  >
                    How to Build a Following
                  </Link>
                </div>
              }
            />

            <Navbar
              navbarIcon={faDollarSign}
              navbarSection="Earning Revenue"
              navbarSubsection={
                <div>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/how-payment-works"
                  >
                    How Payment Works
                  </Link>
                  <Link
                    className={s.link}
                    to="/handbook/earning-revenue/connecting-with-the-community"
                  >
                    Connecting with the Community
                  </Link>
                </div>
              }
            />

            <Link className={s.newProfileLink} to="/user/profiles/new">
              Create a shadowing profile
            </Link>
          </div>

          <div className={s.explanation}>
            <Panel>
              <Panel.Heading>
                <div>Planning a Session</div>
              </Panel.Heading>
              <Panel.Body>
                <div className={s.handbookTitle}>A guide for experts</div>
                <div className={s.subsectionTitle}>Interactivity Level</div>

                <img
                  alt="craftsman"
                  src="/assets/images/handbook/craftsman@1280x600.jpg"
                />

                <p>
                  You have a variety of options when it comes to creating
                  sessions and it's worth noting that you can change each of
                  these options on a session-by-session basis, so feel free to
                  experiment with a combination that best suits your schedule,
                  area of expertise or line of work.
                </p>

                <p>
                  When creating a session, the first option you should consider
                  is how interactive you want it to be, which can range from
                  observation-only to fully interactive.
                </p>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Observation-Only Sessions</strong>
                  </div>
                  <p>
                    During observation-only sessions, your shadowers will follow
                    you around your workplace as you perform your regular
                    duties.
                  </p>

                  <p>
                    Please note that, even though the session is not fully
                    interactive, with hands-on projects, you are still expected
                    to:
                  </p>

                  <ul>
                    <li>Provide a full explanation of anything you do</li>

                    <li>Answer any questions your shadower might have</li>

                    <li>
                      Show all of the necessary steps to achieve the desired
                      outcome
                    </li>
                  </ul>

                  <p>
                    During observation-only sessions, the shadower is expected
                    to remain somewhat of a "fly-on-the-wall," refraining from
                    interfering too often. It is up to you to determine what is
                    allowed during observation-only sessions, which you can
                    specify in the Shadowing Guidelines section of your
                    shadowing profile.
                  </p>

                  <p>
                    To signify that a session is observation-only, click the
                    checkbox under the pair of glasses.
                  </p>
                </div>

                <div className={s.contentContainer}>
                  <div>
                    <strong>Interactive Sessions</strong>
                  </div>
                  <p>
                    Interactive sessions provide shadowers a unique opportunity
                    to flex their creative muscles and really step from the
                    amateur realm into the professional sphere. There are no
                    hard-and-fast rules about what defines an interactive
                    session, but a session could be considered interactive if
                    your shadower:
                  </p>

                  <ul>
                    <li>Helps you set up or tear down equipment</li>

                    <li>Assists you in a current work-related project</li>

                    <li>Works with you on a project created just for them</li>

                    <li>
                      Receives instruction and help from you on a personal
                      project of theirs
                    </li>

                    <li>Operates your tools in a real-working environment</li>
                  </ul>

                  <p>
                    If you plan to create a project for your shadower to work on
                    or complete during the session, check out the{' '}
                    <Link to="/handbook/planning-a-session/providing-resources">
                      Providing Resources
                    </Link>{' '}
                    section.
                  </p>
                </div>

                <ShadowerLink
                  className={s.nextLink}
                  to="/handbook/planning-a-session/session-type"
                >
                  Next {'>'}
                </ShadowerLink>
              </Panel.Body>
            </Panel>
          </div>
        </div>
      </PageLayout>
    );
  }
}

export default withStyles(s)(InteractivityLevelPage);
